import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, ExternalLink, AlertCircle, Loader2 } from 'lucide-react';
import { auth } from '../config/firebase';

interface AuthLinkValidation {
  email: string;
  firstName: string;
}

const AmazonLink: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [authData, setAuthData] = useState<AuthLinkValidation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);


  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');

    if (!token) {
      setError('Invalid authentication link - missing token.');
      setLoading(false);
      return;
    }

    validateToken(token);
  }, [location]);

  const validateToken = async (token: string) => {
    try {
      setValidating(true);
      
      const response = await fetch('https://us-central1-jls-apps.cloudfunctions.net/validateAuthLink', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });
      
      const result = await response.json();
      
      if (response.ok && result.valid) {
        setAuthData({
          email: result.email,
          firstName: result.firstName
        });
        setError(null);
      } else {
        setError('This authentication link has expired or is invalid.');
      }
    } catch (err: any) {
      console.error('Token validation failed:', err);
      setError('This authentication link has expired or is invalid.');
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleConnectAmazon = async () => {
    if (!authData) return;

    try {
      setLoading(true);
      setError(null);

      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');
      
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }
      
      const idToken = await user.getIdToken();

      const response = await fetch('https://us-central1-jls-apps.cloudfunctions.net/initiateAmazonAuth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          email: authData.email,
          authToken: token
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to initiate Amazon authentication');
      }
      
      if (result.authUrl) {
        // Redirect to Amazon OAuth
        window.location.href = result.authUrl;
      } else {
        throw new Error('Failed to generate Amazon authorization URL');
      }
    } catch (err: any) {
      console.error('Amazon auth initiation failed:', err);
      setError(err.message || 'Failed to connect to Amazon. Please try again.');
      setLoading(false);
    }
  };

  const handleGoToApp = () => {
    navigate('/sb-search-term-report');
  };

  if (loading && !validating) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-amber-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Loading...
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please wait while we process your request
          </p>
        </div>
      </div>
    );
  }

  if (error && !authData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="flex justify-center mb-6">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
            
            <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900 mb-4">
              Invalid Link
            </h2>
            
            <Alert className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <p className="text-center text-sm text-gray-600">
                This could happen if:
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                  The link has expired (links are valid for 24 hours)
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                  The link has already been used
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                  The link was copied incorrectly
                </li>
              </ul>
              
              <p className="text-center text-sm text-gray-600 pt-4">
                Please contact your administrator for a new authentication link.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="flex justify-center mb-6">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            
            <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900 mb-4">
              Connection Successful!
            </h2>
            
            <p className="text-center text-sm text-gray-600 mb-6">
              Your Amazon Advertising account has been successfully connected.
            </p>

            <Button 
              onClick={handleGoToApp}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            >
              Go to SB Search Term Report
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-amber-100 rounded-full">
              <ExternalLink className="h-8 w-8 text-amber-600" />
            </div>
          </div>
          
          <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900 mb-2">
            Connect Amazon Account
          </h2>
          
          <p className="text-center text-sm text-gray-600 mb-6">
            Hi {authData?.firstName}! You're about to connect your Amazon Advertising account.
          </p>

          {validating && (
            <Alert className="mb-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Validating authentication link...
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-red-700">
                {error}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4 mb-6">
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="font-medium text-gray-900 mb-2">What will happen:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></span>
                  You'll be redirected to Amazon
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></span>
                  Log in to your Amazon Advertising account
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></span>
                  Select which advertising profile to connect
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></span>
                  Authorize access to your advertising data
                </li>
              </ul>
            </div>
            
            <div className="border rounded-lg p-4 bg-blue-50">
              <h3 className="font-medium text-gray-900 mb-2">We'll access:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Sponsored Brands search term reports</li>
                <li>• Sponsored Products search term reports</li>
                <li>• Sponsored Products advertised product reports</li>
              </ul>
            </div>
          </div>

          <Button 
            onClick={handleConnectAmazon}
            disabled={loading || validating || !authData}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect to Amazon
              </>
            )}
          </Button>

          <p className="mt-4 text-center text-xs text-gray-500">
            You'll be redirected to Amazon's secure authentication page
          </p>
        </div>
      </div>
    </div>
  );
};

export default AmazonLink;